/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import ProductTileImage from './partials/product-tile-image'

// Components
import {
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    Stack,
    useMultiStyleConfig,
    Button,
    useMediaQuery,
    Flex,
} from '@chakra-ui/react'

// Hooks
import {FormattedMessage} from 'react-intl'

// Other
import {useCompare} from '../../context/compare'
import {breakpoints, remCalc} from '../../theme/_utils'
import EnergyLabel from '../energy-label'
import NotifyMeButton from '../notify-me-button'
import {PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID} from '../../constants'
import {AddToCartIcon, CloudIcon, SizeIcon, BeanIcon} from '../icons'
import {
    getPriceData,
    getVariantFromRepresentedProduct,
    getBundleId,
} from '../../utils/product-utils'
import {getOrderAvailabilityStatus} from '../../utils/order-availability-utils'
import useProductData from '../../hooks/use-product-data'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

import loadable from '@loadable/component'
const ReviewScoreIcon = loadable(() => import('../review/ReviewScoreIcon'))

import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'

import {
    basic_gtm_event,
    item_gtm_event,
    polish_ecommerce_gtm_event,
} from '../../custom-api/layers/default'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useCorporateSale from '../../hooks/use-corporate-sale'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import SwatchSection from './partials/swatch-section'
import PriceSection from './partials/price-section'
import Link from '@salesforce/retail-react-app/app/components/link'
//import usePlpReviews from './hooks/use-plp-reviews'

// Component Skeleton
export const Skeleton = () => {
    const styles = useMultiStyleConfig('ProductTile')

    return (
        <Box data-testid="sf-product-tile-skeleton">
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <ChakraSkeleton width="80px" height="20px" />
                <ChakraSkeleton width={{base: '120px', md: '220px'}} height="12px" />
            </Stack>
        </Box>
    )
}

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = ({
    dynamicImageProps,
    product,
    productIndex = undefined,
    gridView = false,
    shouldHideReviews = false,
    shouldHideVariants = false,
    shouldHideCompare = false,
    selectableAttributeId = PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID,
    //badgeDetails = PRODUCT_BADGE_DETAILS,
    categoryName = '',
    // isFromProductList = false,
    isFetchPriority = false,
    defaultBackground = 'background.neutral.default',
    imageStyle = {},
    ...rest
}) => {
    const [isDesktop] = useMediaQuery(`(min-width: ${breakpoints.lg})`)
    const [isMobile] = useMediaQuery(`(max-width: ${breakpoints.md})`)
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const [isHovered, setIsHovered] = useState(false)
    const compactView = gridView && isMobile
    const {addLayer} = useGoogleTagManager()
    const containerRef = useRef()
    const {isCorporateSale, corporateSaleLabelName} = useCorporateSale(product)
    const navigate = useNavigation()
    const {locale} = useMultiSite()
    const localeId = locale?.alias
    const {currency} = useCurrency()
    // const {hasReviewContent, reviewContainerRef} = usePlpReviews({
    //     productId: product?.productId,
    //     shouldHideReviews,
    // })
    const styles = useMultiStyleConfig('ProductTile', {
        variant: isDesktop ? 'collapsable' : compactView ? 'compact' : 'default',
        shouldHideVariants,
    })

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName =
        product?.representedProduct?.c_variantIdentifier ||
        product?.c_variantIdentifier ||
        product?.name ||
        product?.productName

    const {productUrl, priceData} = useProductData(product, selectableAttributeId)

    const compareContext = useCompare() || {
        state: {compareItems: []},
        addItem: () => {},
        removeItem: () => {},
        compareBannerRef: {current: null},
    }

    const {
        state: {compareItems},
        addItem,
        removeItem,
        compareBannerRef,
    } = compareContext
    const maxCompareItems = isMobile ? 2 : 3
    const isProductCompared = compareItems.some(
        (compareItem) => compareItem.productId === product.productId,
    )
    const isCompareLimitReached = compareItems.length >= maxCompareItems && !isProductCompared

    const onCompareSelect = (product) => {
        if (isProductCompared) {
            removeItem(product)
        } else if (compareItems.length < maxCompareItems) {
            addItem(product)
            setTimeout(() => {
                if (compareBannerRef?.current) {
                    compareBannerRef.current.focus()
                }
            }, 100)
        }
    }

    const variant = getVariantFromRepresentedProduct(product)

    const selectItemTileEventGtm = async () => {
        if (shouldHideReviews && shouldHideVariants && shouldHideCompare) {
            await addLayer(
                basic_gtm_event.bind(this, {
                    event: 'help_me_choose',
                    eventCategory: 'engagement',
                    eventAction: 'help_me_choose-product_click',
                    eventLabel: product.id,
                }),
            )
        }
        await addLayer(polish_ecommerce_gtm_event.bind(this))

        await addLayer(
            item_gtm_event.bind(this, {
                event: 'ga_event',
                event_name: 'select_item',
                item_list_name: categoryName,
                bundle: product?.c_isBundle,
                bundle_id: getBundleId(product),
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: product?.representedProduct?.id,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.productId,
                        price: +(variant?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        // get_reevoo_rating() || get_bv_rating(),
                        erp_category: product?.representedProduct?.c_materialGroup,
                        prdha_category: product?.c_prdha_category,
                        index: productIndex + 1,
                        stock_status:
                            product?.c_extendVariant?.stock > 0 ? 'in-stock' : 'out-of-stock',
                    },
                ],
            }),
        )
    }

    let inventorySize = null

    if ('c_extendVariant' in product && product.c_extendVariant) {
        inventorySize = product?.c_extendVariant?.stock
    } else if ('inventory' in product && product.inventory) {
        inventorySize = product?.inventory?.stockLevel
    }

    // Determine order availability status and conditions
    const orderStatus = getOrderAvailabilityStatus(product)
    const showPreorder = orderStatus.isPreorder
    const showOutOfStock =
        (inventorySize === 0 || inventorySize === null) &&
        !orderStatus.isBackorder &&
        !orderStatus.isPreorder
    const showAddToCart =
        !notShoppableCountry &&
        (inventorySize > 0 || orderStatus.isBackorder || orderStatus.isPreorder)
    const showNotifyMe =
        !orderStatus.isBackorder &&
        !orderStatus.isPreorder &&
        (inventorySize === 0 || inventorySize === null)

    const handleAddToCart = () => {
        navigate(`/${localeId}${productUrl}`)
    }
    const keyFeature1 = product?.representedProduct?.c_PLPkeyFeature1 ?? null
    const keyFeature2 = product?.representedProduct?.c_PLPkeyFeature2 ?? null
    const keyFeature3 = product?.representedProduct?.c_PLPkeyFeature3 ?? null

    const keyFeatures = [
        {icon: CloudIcon, text: keyFeature1},
        {icon: BeanIcon, text: keyFeature2},
        {icon: SizeIcon, text: keyFeature3},
    ]
    var feefoReviewProductId =
        product?.c_bundleType === 'Flexible'
            ? product?.c_primaryProductMasterId
            : product?.c_prdha_category || product?.productId

    var bazaarReviewProductId =
        product?.c_bundleType === 'Flexible'
            ? product?.c_firstPrimaryProductId
            : product?.representedProduct?.c_subModelIdentifier || product?.productId

    const hasSwatches = product?.c_enabledVariationGroups?.length > 0
    const [hasVisibleReview, setHasVisibleReview] = useState(false)
    const shouldReserveReviewSwatchSpace = !hasVisibleReview && !hasSwatches

    const tileBackground = isHovered ? 'background.neutral.hover' : defaultBackground

    useEffect(() => {
        setHasVisibleReview(false)
    }, [bazaarReviewProductId, feefoReviewProductId, shouldHideReviews])

    return (
        <Box
            {...styles.container}
            ref={containerRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsHovered(true)}
            onBlur={(event) => {
                if (!containerRef.current.contains(event.relatedTarget)) {
                    setIsHovered(false)
                }
            }}
            background={tileBackground}
        >
            <Box
                data-testid="product-tile"
                {...rest}
                position="relative"
                display="flex"
                flexDir="column"
                borderRadius="large-1"
                h="100%"
                background={tileBackground}
            >
                <ProductTileImage
                    dynamicImageProps={dynamicImageProps}
                    product={product}
                    productUrl={productUrl}
                    compactView={compactView}
                    priceData={priceData}
                    isOfficina={product?.representedProduct?.c_isOfficina === 'true'}
                    isCorporateSale={isCorporateSale}
                    corporateSaleLabelName={corporateSaleLabelName}
                    variant={shouldHideVariants ? 'transparent' : ''}
                    selectItemTileEventGtm={selectItemTileEventGtm}
                    isFetchPriority={isFetchPriority}
                    showOutOfStock={showOutOfStock}
                    onCompareSelect={shouldHideCompare ? undefined : onCompareSelect}
                    compareDisabled={isCompareLimitReached}
                    isCompareActive={isProductCompared}
                    isHovered={isHovered}
                    imageStyle={imageStyle}
                />

                <Flex {...styles?.bottomContainer} flex="1" justifyContent="space-between">
                    <Flex {...styles?.headerRow} justifyContent="space-between">
                        <Flex
                            {...styles?.subheaderRow}
                            minHeight={shouldReserveReviewSwatchSpace ? remCalc(24) : undefined}
                        >
                            {!shouldHideReviews && (
                                <ReviewScoreIcon
                                    id={bazaarReviewProductId}
                                    parentSku={feefoReviewProductId}
                                    variant={'plpReview'}
                                    onVisibilityChange={setHasVisibleReview}
                                />
                            )}
                            <SwatchSection swatchInfo={product?.c_enabledVariationGroups} />
                        </Flex>
                        <Flex maxWidth={remCalc(42)} maxHeight={remCalc(22)}>
                            {(product?.representedProduct?.c_energyEfficiencyClass ||
                                product?.c_energyEfficiencyClass) && (
                                <Box {...styles.energyLabel}>
                                    <EnergyLabel
                                        level={
                                            product?.representedProduct?.c_energyEfficiencyClass ||
                                            product?.c_energyEfficiencyClass
                                        }
                                        labelPosition="right"
                                        manualDocument={
                                            product?.c_manualDocument ||
                                            product?.representedProduct?.c_manualDocument
                                        }
                                        isPlp={true}
                                    />
                                </Box>
                            )}
                        </Flex>
                    </Flex>
                    <Flex
                        width="100%"
                        flex="1"
                        flexDir="column"
                        gap="xsmall.lg"
                        data-testid="product-tile-info-section"
                    >
                        <Flex {...styles?.body}>
                            <Flex width="100%">
                                <Text as="h2" {...styles.title} width="100%">
                                    <Link
                                        href={`${productUrl}`}
                                        color="inherit"
                                        textDecoration="none"
                                        _hover={{textDecoration: 'none'}}
                                    >
                                        {localizedProductName}
                                    </Link>
                                </Text>
                            </Flex>
                            <Flex {...styles?.keyFeaturesContainer} maxWidth="100%">
                                {keyFeatures.length > 0 && (
                                    <Link
                                        href={`${productUrl}`}
                                        display="contents"
                                        color="inherit"
                                        textDecoration="none"
                                        _hover={{textDecoration: 'none'}}
                                    >
                                        <Flex {...styles?.keyFeature} maxWidth="100%">
                                            {keyFeatures.map((feature, idx) => {
                                                const FeatureIcon = feature?.icon
                                                return (
                                                    feature?.text !== null && (
                                                        <Flex
                                                            key={`key-feature-${feature}-${idx}`}
                                                            gap="xxxsmall.base"
                                                        >
                                                            {FeatureIcon && (
                                                                <FeatureIcon
                                                                    width={remCalc(14)}
                                                                    height={remCalc(14)}
                                                                    color="foreground.neutral.default"
                                                                />
                                                            )}
                                                            <Text
                                                                {...styles.featureListElement}
                                                                noOfLines={2}
                                                            >
                                                                {feature?.text}
                                                            </Text>
                                                        </Flex>
                                                    )
                                                )
                                            })}
                                        </Flex>
                                    </Link>
                                )}
                            </Flex>
                        </Flex>
                        <Flex {...styles?.footerRow}>
                            <Flex w="100%">
                                <Link
                                    href={`${productUrl}`}
                                    display="contents"
                                    color="inherit"
                                    textDecoration="none"
                                    _hover={{textDecoration: 'none'}}
                                >
                                    <PriceSection
                                        priceData={priceData}
                                        currency={currency}
                                        notShoppableCountry={notShoppableCountry}
                                    />
                                </Link>
                            </Flex>
                            <Flex data-testid="product-cta-buttons">
                                {showPreorder && (
                                    <Button
                                        size="large"
                                        width="100%"
                                        variant="outline"
                                        borderRadius="lg"
                                        onClick={handleAddToCart}
                                        data-testid="preorder-button"
                                    >
                                        <FormattedMessage
                                            id="product_tile.preorder"
                                            defaultMessage="Pre-order"
                                        />
                                    </Button>
                                )}
                                {!showPreorder && showNotifyMe && (
                                    <NotifyMeButton
                                        product={product}
                                        compactView={compactView}
                                        isPlp={true}
                                    />
                                )}
                                {/**New tiles CTA goes to pdp instead of add to cart */}
                                {!showPreorder && showAddToCart && (
                                    <Button
                                        size="small"
                                        variant={
                                            isHovered
                                                ? 'icon-button-primary'
                                                : 'icon-button-outline'
                                        }
                                        leftIcon={
                                            <AddToCartIcon
                                                width={remCalc(16)}
                                                height={remCalc(16)}
                                                color={
                                                    isHovered
                                                        ? 'foreground.neutral.inverse'
                                                        : 'foreground.neutral.default'
                                                }
                                                fill="none"
                                            />
                                        }
                                        onClick={handleAddToCart}
                                        paddingX={remCalc(10)}
                                        paddingY={remCalc(10)}
                                    />
                                )}
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Box>
        </Box>
    )
}

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        id: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string,
        }),
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        c_variantIdentifier: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        c_defaultName: PropTypes.string,
        c_defaultCategoryName: PropTypes.string,
        c_taxRate: PropTypes.number,
        c_prdha_category: PropTypes.string,
        productId: PropTypes.string,
        productPromotions: PropTypes.array,
        representedProduct: PropTypes.object,
        productType: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variationGroups: PropTypes.array,
        variants: PropTypes.array,
        c_categoryName: PropTypes.string,
        c_extendVariant: PropTypes.object,
        inventory: PropTypes.object,
        c_subModelIdentifier: PropTypes.string,
        c_bundleType: PropTypes.string,
        c_isBundle: PropTypes.string,
        c_firstPrimaryProductId: PropTypes.string,
        c_omnibusPrice: PropTypes.shape({
            omnibusPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        }),
        c_energyEfficiencyClass: PropTypes.string,
        c_manualDocument: PropTypes.string,
        type: PropTypes.shape({
            set: PropTypes.bool,
            bundle: PropTypes.bool,
            item: PropTypes.bool,
        }),
        c_enabledVariationGroups: PropTypes.array,
        c_primaryProductMasterId: PropTypes.string,
    }),
    productIndex: PropTypes.number,
    categoryName: PropTypes.string,
    /**
     * Props to hide collapsable sections & features (example: HelpMeChoose page)
     */
    shouldHideReviews: PropTypes.bool,
    shouldHideVariants: PropTypes.bool,
    shouldHideCompare: PropTypes.bool,
    /**
     * Display the product in a compact view. defaults to false
     */
    gridView: PropTypes.bool,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    imageStyle: PropTypes.object,
    /**
     * Display the product in a product list (ex: PLP grid)
     */
    isFromProductList: PropTypes.bool,

    isFetchPriority: PropTypes.bool,
    defaultBackground: PropTypes.string,
}

export default ProductTile
